// src/components/ImprovedMomentDetailLayout.tsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Info } from "lucide-react";
import DetailedDescriptionWidget from "./DetailedDescription";
import EnhancedParticipantsWidget from "./EnhancedParticipantsWidget";
import { ProcessItem } from "@/types";

interface MomentDetailProps {
  activeProcessData: any;  // Använd mer specifik typ när den är tillgänglig
  originalProcessItem: ProcessItem | undefined;
  filteredRoles: string[];
}

const ImprovedMomentDetailLayout: React.FC<MomentDetailProps> = ({ 
  activeProcessData, 
  originalProcessItem,
  filteredRoles
}) => {
  // Om inget aktivt momentdata finns, visa en infotext
  if (!activeProcessData) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center text-gray-400 py-8 bg-gray-800/20 border-gray-700/50 rounded-xl border p-6"
      >
        <p className="mb-2">Välj ett moment för att se detaljer</p>
        <p className="text-xs text-gray-500">Momentdetaljer visas här när du väljer ett moment från listan</p>
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeProcessData.title}
        layout
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        transition={{ duration: 0.3 }}
      >
        {/* Momenthuvud med titel */}
        <div className="scroll-to-description mb-6 bg-gray-800/20 border-gray-700/50 backdrop-blur-md rounded-xl border overflow-hidden">
          {/* Moment rubrik och nummer */}
          <div className="p-6 border-b border-gray-700/30">
            <h2 className="text-2xl font-medium text-white flex items-center">
              <span className="font-mono text-sm text-orange-300 mr-3">{activeProcessData.hierarchicalNumber}</span>
              {activeProcessData.title}
            </h2>
          </div>
          
          {/* Huvudinnehåll utan rubrik */}
          <div className="p-6">
            {/* Beskrivning direkt utan rubrik */}
            <div className="bg-gray-800/50 rounded-lg p-4">
              {activeProcessData.description ? (
                <p className="text-gray-300 text-sm leading-relaxed">
                  {activeProcessData.description}
                </p>
              ) : (
                <p className="text-gray-400 italic">Ingen beskrivning tillgänglig.</p>
              )}
            </div>
            
            {/* Viktigt-sektion efter beskrivningen */}
            {activeProcessData.viktigt && activeProcessData.viktigt.trim() !== "" && (
              <div className="mt-4 bg-red-500/10 border-l-4 border-red-500 rounded-r-md p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
                  <span className="text-sm text-red-300 font-medium">Viktigt</span>
                </div>
                <p className="text-red-300 text-sm leading-relaxed">{activeProcessData.viktigt}</p>
              </div>
            )}
            
            {/* Detaljerad beskrivning som expanderbar del */}
            {activeProcessData.detailedDescription && (
              <div className="mt-4">
                <DetailedDescriptionWidget 
                  detailedDescription={activeProcessData.detailedDescription} 
                />
              </div>
            )}
          </div>
        </div>

        {/* Deltagare & ansvar widget - visas om originalProcessItem finns */}
        <div className="scroll-to-participants">
          {originalProcessItem && (
            <EnhancedParticipantsWidget 
              processItem={originalProcessItem} 
              filteredRoles={filteredRoles}
            />
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImprovedMomentDetailLayout;